import { centsToFormattedDollars, currentApy } from '@fruitful/fruitils';
import {
    PremiumCardPolicy,
    type SubscriptionProduct,
    SubscriptionProductId,
} from '@fruitful/types';

import type { User } from '../../user/types/User';

export const getSubscriptionBenefits = (
    product: SubscriptionProduct,
): string[] => {
    const cashAPY = product.banking.rateCashAPY ?? currentApy();
    const cashBack = product.banking.rateCashBack ?? '1%';
    const aumThreshold = product.banking.thresholdCentsInvestAUM ?? 0;
    const aumFee = product.banking.rateInvestAUM ?? '0%';
    const formattedAumThreshold = centsToFormattedDollars(aumThreshold, false);

    const moneySystemBenefit =
        'A built-for-you money system that automates your finances';
    const cashApyBenefit = `${cashAPY} APY¹ on all of your cash`;
    const cashBackBenefit = `${cashBack} cash back² on card purchases`;
    const investmentManagementBenefit =
        aumThreshold > 0
            ? `Professional investment management with 0% AUM fees below ${formattedAumThreshold} (${aumFee} AUM above)`
            : `Professional investment management with ${aumFee} AUM fees`;

    const PREMIUM_TRIAL_BENEFITS = [
        moneySystemBenefit,
        'Two 1-on-1 sessions up front to get you on track to hit your goals with confidence',
        'Your own dedicated Fruitful Guide',
        'Anytime messaging',
        cashApyBenefit,
        cashBackBenefit,
        investmentManagementBenefit,
    ];

    const PREMIUM_SUBSCRIPTION_BENEFITS = [
        moneySystemBenefit,
        'Your own dedicated Fruitful Guide',
        'Three on-demand 1-on-1 sessions each year',
        'Anytime messaging',
        'Premium physical card',
        cashApyBenefit,
        cashBackBenefit,
        investmentManagementBenefit,
    ];

    const STANDARD_SUBSCRIPTION_BENEFITS = [
        'No membership fees',
        moneySystemBenefit,
        cashApyBenefit,
        cashBackBenefit,
        investmentManagementBenefit,
    ];

    const benefitsMap: Record<SubscriptionProductId, string[]> = {
        [SubscriptionProductId.TRIAL_PREMIUM]: PREMIUM_TRIAL_BENEFITS,
        [SubscriptionProductId.PREMIUM]: PREMIUM_SUBSCRIPTION_BENEFITS,
        [SubscriptionProductId.STANDARD]: STANDARD_SUBSCRIPTION_BENEFITS,
        [SubscriptionProductId.ESSENTIAL]: ESSENTIAL_SUBSCRIPTION_BENEFITS,
        [SubscriptionProductId.PLUS]: PLUS_SUBSCRIPTION_BENEFITS,
        [SubscriptionProductId.SOLO]: LEGACY_SUBSCRIPTION_BENEFITS,
        [SubscriptionProductId.JOINT]: LEGACY_SUBSCRIPTION_BENEFITS,
    };

    return benefitsMap[product.id];
};

const PREMIUM_PERKS = [
    'Your own dedicated Fruitful Guide',
    'Three live sessions each year with your Guide plus anytime messaging',
    'Premium card (coming soon)',
];

/** The "You've unlocked" lines on the Premium welcome sheet, from the product's catalog rates. */
export const getPremiumUnlockedBenefits = (
    product: SubscriptionProduct,
): string[] => [
    `${product.banking.rateCashAPY} APY on all cash`,
    `${product.banking.rateCashBack} cash back on all purchases`,
    `${formatAumFeeTileValue(product)} AUM fees on investments`,
    'Your own dedicated Fruitful guide',
];

/** No dedicated "member since" field exists yet for all membership types, so fall back to account creation. */
export const getMemberSinceDate = (user: User): string =>
    user.membership?.createdAt ?? user.createdAt;

export const TRIAL_UPGRADE_PERKS = [
    'Three live sessions each year with your Guide',
    'Premium card (coming soon)',
];

export const getMembershipPerks = (
    productId: SubscriptionProductId,
): string[] => {
    switch (productId) {
        case SubscriptionProductId.PREMIUM:
            return PREMIUM_PERKS;
        case SubscriptionProductId.TRIAL_PREMIUM:
            return [
                'Your own dedicated Fruitful Guide',
                'Two live sessions up front to get you on track to hit your goals with confidence plus anytime messaging',
            ];
        case SubscriptionProductId.STANDARD:
            return [
                'No membership fees',
                'A built-for-you money system that automates your finances',
            ];
        default:
            return [];
    }
};

export const formatAumFeeTileValue = (product: SubscriptionProduct): string =>
    product.banking.thresholdCentsInvestAUM > 0
        ? formatAumFeeLines(product).join(' ')
        : product.banking.rateInvestAUM;

/**
 * Compact phrasing of the same AUM fee data `getSubscriptionBenefits` uses,
 * as separate lines for a table cell (e.g. ["0.25%", "above $10K"]) — the
 * design abbreviates the threshold ("$10K") rather than spelling it out.
 */
export const formatAumFeeLines = (product: SubscriptionProduct): string[] => {
    const { thresholdCentsInvestAUM, rateInvestAUM } = product.banking;
    if (thresholdCentsInvestAUM <= 0) {
        return [rateInvestAUM === '0%' ? 'No fee' : rateInvestAUM];
    }
    const thresholdThousands = Math.round(thresholdCentsInvestAUM / 100_000);
    return [rateInvestAUM, `above $${thresholdThousands}K`];
};

/** "Not included" / "N sessions annually", derived from the catalog's own general-session allowances. */
export const formatGeneralSessionsLabel = (
    product: SubscriptionProduct,
): string => {
    const totalAnnual = product.guidance.generalSessions.reduce(
        (sum, session) => sum + (session.annualLimit ?? 0),
        0,
    );
    if (totalAnnual <= 0) {
        return 'Not included';
    }
    return `${totalAnnual} session${totalAnnual === 1 ? '' : 's'} annually`;
};

export const formatGuideMessagingLabel = (
    product: SubscriptionProduct,
): string =>
    product.guidance.includesAssignedGuide ? 'Unlimited' : 'Not included';

export const formatPremiumCardLabel = (product: SubscriptionProduct): string =>
    product.banking.premiumCardPolicy === PremiumCardPolicy.NONE
        ? 'Not included'
        : 'Coming soon';

export const getLegacySubscriptionBenefits = (
    type: SubscriptionProductId | undefined,
): string[] => {
    switch (type) {
        case SubscriptionProductId.SOLO:
        case SubscriptionProductId.JOINT:
            return LEGACY_SUBSCRIPTION_BENEFITS;
        case SubscriptionProductId.ESSENTIAL:
            return ESSENTIAL_SUBSCRIPTION_BENEFITS;
        case SubscriptionProductId.PLUS:
            return PLUS_SUBSCRIPTION_BENEFITS;
        default:
            return [];
    }
};

// Rendered content was not changed during 'commercial model evolution' effort
const LEGACY_SUBSCRIPTION_BENEFITS = [
    'Expert advice and support from your dedicated Guide, a CFP® Professional',
    '1-to-1 video sessions focused on hitting your goals with progress check-ins, quick syncs, and anytime messaging',
    `${currentApy()} APY¹ on all of your cash`,
    'Tailored investment portfolios with expert support at every step, and no management fees',
];

// Rendered content was not changed during 'commercial model evolution' effort
const ESSENTIAL_SUBSCRIPTION_BENEFITS = [
    'Your own dedicated Fruitful Guide',
    'Three 1-on-1 sessions that get you on track to hit your goals with confidence',
    'A built-for-you money system that automates your finances',
    'Annual check-in session to stay on track',
    'Anytime messaging',
    `${currentApy()} APY¹ on all of your cash`,
    'Professional investment management',
];

const PLUS_SUBSCRIPTION_BENEFITS = [
    'On-demand 1-on-1 sessions anytime',
    ...ESSENTIAL_SUBSCRIPTION_BENEFITS,
];
